import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import {
  ResolveLoginErrorCode,
  mapLoginErrorCodeToRoute,
} from 'src/app/core/authentication/resolve-login-error-code';
import { environment } from '../../../../environments/environment';

/**
 * Service responsible for handling MSAL authentication redirects and error states.
 * Extracted from DeepLinkService to improve separation of concerns.
 */
@Injectable({ providedIn: 'root' })
export class MsalRedirectService {
  private static readonly LOGOUT_PENDING_KEY = 'hb_logout_pending';
  private readonly router = inject(Router);
  private readonly msalService = inject(MsalService);

  private readonly redirectUri: string = environment.msalConfig.auth.redirectUri;

  /**
   * Process MSAL redirect with retry logic for Android compatibility.
   * @param fragment The URL fragment portion (without leading '#')
   * @param hasAuthQuery Whether the original URL had authentication query parameters
   */
  processMsalRedirect(fragment: string, hasAuthQuery: boolean): void {
    try {
      this.attemptMsalRedirectHandling(fragment, hasAuthQuery);
    } catch {
      this.scheduleRedirectRoute(fragment);
    }
  }

  /**
   * Handle authentication error fragments and navigate to appropriate error routes.
   * @param rawFragment The URL fragment portion (without leading '#')
   * @returns true if navigation occurred, false otherwise
   */
  handleAuthErrorFragment(rawFragment: string): boolean {
    if (!rawFragment) return false;

    const params = new URLSearchParams(rawFragment);
    const error = params.get('error');
    const errorDescription = params.get('error_description') || '';

    if (!error && !errorDescription) return false;

    const resolvedCode = ResolveLoginErrorCode({ error, error_description: errorDescription });
    if (resolvedCode === 'Unknown') return false;

    const dedicatedRoute = mapLoginErrorCodeToRoute(resolvedCode);

    // Clear MSAL state to prevent stuck authentication
    this.clearAllMsalState();

    (dedicatedRoute
      ? this.router.navigate([dedicatedRoute], { replaceUrl: true })
      : this.router.navigate(['/auth/login-failed'], {
          replaceUrl: true,
          queryParams: { errorCode: resolvedCode },
        })
    ).catch((err) => this.handleNavigationError(err));

    return true;
  }

  /**
   * Clear all MSAL-related state from storage to prevent stuck authentication
   */
  private clearAllMsalState(): void {
    // List of known MSAL keys to clear
    const msalKeysToRemove = [
      'msal.interaction.status',
      'msal.interaction.popup',
      'msal.interaction.silent',
      'msal.interaction.redirect',
      'msal.token.request',
      'msal.active.account',
      'msal.redirect.in.progress'
    ];

    // Clear specific keys from both storages
    msalKeysToRemove.forEach(key => {
      try {
        sessionStorage.removeItem(key);
        localStorage.removeItem(key);
      } catch {
        // ignore
      }
    });

    // Clear ALL keys that start with 'msal.' to be thorough
    const clearMsalKeys = (storage: Storage) => {
      const keysToDelete: string[] = [];
      try {
        for (let i = 0; i < storage.length; i++) {
          const key = storage.key(i);
          if (key && (key.startsWith('msal.') || key.includes('msal') && key.includes('interaction'))) {
            keysToDelete.push(key);
          }
        }
        keysToDelete.forEach(key => {
          try {
            storage.removeItem(key);
          } catch {
            // ignore
          }
        });
      } catch {
        // ignore
      }
    };

    clearMsalKeys(sessionStorage);
    clearMsalKeys(localStorage);

    console.log('[MSAL][Redirect] Cleared all MSAL state from storage');
  }

  /**
   * Check if there was a recent logout.
   * @returns true if logout was recent, false otherwise
   */
  wasRecentLogout(): boolean {
    try {
      const pending = localStorage.getItem(MsalRedirectService.LOGOUT_PENDING_KEY);
      if (pending === '1') {
        localStorage.removeItem(MsalRedirectService.LOGOUT_PENDING_KEY);
        return true;
      }
    } catch {
      /* ignore localStorage errors */
    }
    return false;
  }

  /**
   * Attempt to handle MSAL redirect with retry logic for Android compatibility.
   * @param fragment The URL fragment portion (without leading '#')
   * @param hasAuthQuery Whether the original URL had authentication query parameters
   */
  private attemptMsalRedirectHandling(fragment: string, hasAuthQuery: boolean): void {
    const instance = this.msalService.instance;
    const retryState = { attemptedRetry: false };

    const attemptRedirect = (): void => {
      instance
        .handleRedirectPromise()
        .then((result) =>
          this.handleMsalRedirectResult(result, fragment, hasAuthQuery, retryState, attemptRedirect)
        )
        .catch(() =>
          this.handleMsalRedirectError(hasAuthQuery, retryState, attemptRedirect, fragment)
        );
    };

    attemptRedirect();
  }

  /**
   * Handle successful MSAL redirect promise result.
   * @param result The MSAL authentication result
   * @param fragment The URL fragment
   * @param hasAuthQuery Whether auth query parameters were present
   * @param retryState Object tracking retry attempts
   * @param retryFn Function to retry the redirect attempt
   */
  private handleMsalRedirectResult(
    result: unknown,
    fragment: string,
    hasAuthQuery: boolean,
    retryState: { attemptedRetry: boolean },
    retryFn: () => void
  ): void {
    if (!result) {
      // If auth query params are present (Android lost hash), give MSAL one retry chance after a short delay.
      if (hasAuthQuery && !retryState.attemptedRetry) {
        retryState.attemptedRetry = true;
        setTimeout(retryFn, 150); // minimal delay; keeps change low‑risk
        return;
      }

      if (this.shouldNavigateToHome()) {
        return;
      }

      this.scheduleRedirectRoute(fragment);
    }
  }

  /**
   * Handle MSAL redirect promise errors.
   * @param hasAuthQuery Whether auth query parameters were present
   * @param retryState Object tracking retry attempts
   * @param retryFn Function to retry the redirect attempt
   * @param fragment The URL fragment
   */
  private handleMsalRedirectError(
    hasAuthQuery: boolean,
    retryState: { attemptedRetry: boolean },
    retryFn: () => void,
    fragment: string
  ): void {
    if (hasAuthQuery && !retryState.attemptedRetry) {
      retryState.attemptedRetry = true;
      setTimeout(retryFn, 150);
      return;
    }

    this.scheduleRedirectRoute(fragment);
  }

  /**
   * Check if we should navigate to home due to no authenticated accounts.
   * @returns true if navigation occurred, false otherwise
   */
  private shouldNavigateToHome(): boolean {
    try {
      const accounts = this.msalService.instance.getAllAccounts();
      if (!accounts.length) {
        this.router
          .navigate(['/'], { replaceUrl: true })
          .catch((err) => this.handleNavigationError(err));
        return true;
      }
    } catch {
      /* ignore account check errors */
    }
    return false;
  }

  /**
   * Schedule a redirect to the specified fragment
   * @param fragment The URL fragment
   */
  private scheduleRedirectRoute(fragment: string): void {
    setTimeout(() => {
      this.router
        .navigate([this.redirectUri], { replaceUrl: true, fragment })
        .catch((err) => this.handleNavigationError(err));
    }, 50);
  }

  /**
   * Handle navigation errors
   * @param err The navigation error
   */
  private handleNavigationError(err: unknown): void {
    if ((err as { ngNavigationCancellingError?: boolean }).ngNavigationCancellingError) return;
  }
}
